import { useState, useEffect, useRef } from 'react'
import { FiSearch, FiDownload, FiFileText, FiX, FiCalendar, FiDroplet, FiChevronUp, FiChevronDown } from 'react-icons/fi'
import api from '../utils/api'

const fmt = (v, dec = 2) => v == null ? '—' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: dec, maximumFractionDigits: dec })
const fmtInt = (v) => Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })
const _iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function CariRapor() {
  const [q, setQ] = useState('')
  const [sonuc, setSonuc] = useState([])
  const [acik, setAcik] = useState(false)
  const [secili, setSecili] = useState(null)   // {unvan, kimlik, litre}
  const now = new Date()
  const [bas, setBas] = useState(_iso(new Date(now.getFullYear(), now.getMonth(), 1)))
  const [bit, setBit] = useState(_iso(now))
  const [data, setData] = useState(null)
  const [sortDir, setSortDir] = useState('desc')   // işlem tarihi sıralama
  const [urunF, setUrunF] = useState('')           // ürün filtresi
  const [subeF, setSubeF] = useState('')           // şube filtresi
  const [yukleniyor, setYukleniyor] = useState(false)
  const [aramaYuk, setAramaYuk] = useState(false)
  const kutuRef = useRef(null)
  const tmr = useRef(null)

  // Cari arama (debounce)
  useEffect(() => {
    if (secili && q === secili.unvan) return
    clearTimeout(tmr.current)
    tmr.current = setTimeout(async () => {
      setAramaYuk(true)
      try {
        const { data } = await api.get(`/cari-rapor/ara?q=${encodeURIComponent(q)}`)
        setSonuc(data.cariler || [])
        setAcik(true)
      } catch { setSonuc([]) } finally { setAramaYuk(false) }
    }, 250)
    return () => clearTimeout(tmr.current)
  }, [q])

  // Dış tıklama ile dropdown kapat
  useEffect(() => {
    const h = (e) => { if (kutuRef.current && !kutuRef.current.contains(e.target)) setAcik(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const cariSec = (c) => { setSecili(c); setQ(c.unvan); setAcik(false); setData(null) }

  const getir = async () => {
    if (!secili) return
    setYukleniyor(true)
    try {
      const { data } = await api.get(`/cari-rapor/islemler?kimlik=${encodeURIComponent(secili.kimlik)}&unvan=${encodeURIComponent(secili.unvan)}&bas=${bas}&bit=${bit}`)
      setData(data)
    } catch { setData({ islemler: [], islem_sayisi: 0, toplam_litre: 0, toplam_tutar: 0 }) }
    finally { setYukleniyor(false) }
  }

  // Cari + tarih değişince otomatik getir + filtreleri sıfırla
  useEffect(() => { if (secili) getir() /* eslint-disable-next-line */ }, [secili, bas, bit])
  useEffect(() => { setUrunF(''); setSubeF('') }, [data])

  // Filtrelenmiş liste + toplamlar (client-side, anlık)
  const tumIslem = data?.islemler || []
  const urunler = [...new Set(tumIslem.map(r => r.urun).filter(Boolean))]
  const subeler = [...new Set(tumIslem.map(r => r.sube).filter(Boolean))]
  const filtreli = tumIslem.filter(r => (!urunF || r.urun === urunF) && (!subeF || r.sube === subeF))
  const fLitre = filtreli.reduce((s, r) => s + (r.litre || 0), 0)
  const fTutar = filtreli.reduce((s, r) => s + (r.tutar || 0), 0)

  const indir = async (fmt2) => {
    if (!secili) return
    const ff = (urunF ? `&urun=${encodeURIComponent(urunF)}` : '') + (subeF ? `&sube=${encodeURIComponent(subeF)}` : '')
    const url = `/cari-rapor/export/${fmt2}?kimlik=${encodeURIComponent(secili.kimlik)}&unvan=${encodeURIComponent(secili.unvan)}&bas=${bas}&bit=${bit}${ff}`
    const res = await api.get(url, { responseType: 'blob' })
    const blob = new Blob([res.data])
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    const ad = (secili.unvan || 'cari').replace(/[^\wğüşıöçĞÜŞİÖÇ ]/g, '').slice(0, 30).trim()
    a.download = `${ad} - Cari Rapor.${fmt2 === 'excel' ? 'xlsx' : 'pdf'}`
    a.click(); URL.revokeObjectURL(a.href)
  }

  const preset = (tip) => {
    const y = now.getFullYear(), m = now.getMonth()
    if (tip === 'buay') { setBas(_iso(new Date(y, m, 1))); setBit(_iso(now)) }
    else if (tip === 'gecenay') { setBas(_iso(new Date(y, m - 1, 1))); setBit(_iso(new Date(y, m, 0))) }
    else if (tip === 'buyil') { setBas(_iso(new Date(y, 0, 1))); setBit(_iso(now)) }
    else if (tip === 'son30') { setBas(_iso(new Date(now - 29 * 864e5))); setBit(_iso(now)) }
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="mb-5">
        <h1 className="text-lg md:text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <FiDroplet className="text-sky-500" /> Cari Yakıt İşlem Raporu
        </h1>
        <p className="text-xs text-gray-500 mt-0.5">Poçan Petrol — 3 istasyon (Altınekin · Tatlıcak · Ladık) birleşik, VKN bazlı.</p>
      </div>

      {/* Kontroller */}
      <div className="flex flex-col md:flex-row gap-3 md:items-end mb-4">
        {/* Cari arama */}
        <div className="relative flex-1 min-w-0" ref={kutuRef}>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Cari (yazarak ara)</label>
          <div className="relative">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input value={q} onFocus={() => setAcik(true)}
              onChange={(e) => { setQ(e.target.value); if (secili) setSecili(null) }}
              placeholder="Cari ünvanı yazın (ör. KONTUR)..."
              className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-sky-400 outline-none" />
            {q && <FiX onClick={() => { setQ(''); setSecili(null); setData(null) }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600" size={15} />}
          </div>
          {acik && (
            <div className="absolute z-30 mt-1 w-full max-h-80 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
              {aramaYuk && <div className="px-3 py-2 text-xs text-gray-400">Aranıyor…</div>}
              {!aramaYuk && sonuc.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Sonuç yok</div>}
              {sonuc.map((c) => (
                <div key={c.kimlik} onClick={() => cariSec(c)}
                  className="px-3 py-2 cursor-pointer hover:bg-sky-50 dark:hover:bg-sky-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0">
                  <div className="text-sm text-gray-800 dark:text-gray-100 truncate">{c.unvan}</div>
                  <div className="text-[11px] text-gray-400 flex justify-between">
                    <span>{fmtInt(c.litre)} lt · toplam</span>
                    <span>son: {c.son_islem}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Tarih */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Başlangıç</label>
          <input type="date" value={bas} max={bit} onChange={(e) => setBas(e.target.value)}
            className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Bitiş</label>
          <input type="date" value={bit} min={bas} onChange={(e) => setBit(e.target.value)}
            className="px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-100" />
        </div>
      </div>

      {/* Hazır dönem butonları */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {[['buay', 'Bu Ay'], ['gecenay', 'Geçen Ay'], ['son30', 'Son 30 Gün'], ['buyil', 'Bu Yıl']].map(([k, l]) => (
          <button key={k} onClick={() => preset(k)}
            className="px-3 py-1 text-xs rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">{l}</button>
        ))}
      </div>

      {!secili && <div className="text-center py-16 text-gray-400 text-sm">Rapor için yukarıdan bir cari seçin.</div>}

      {secili && (
        <>
          {/* Özet + export */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3 p-3 bg-slate-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4">
              <div>
                <div className="text-[10px] text-gray-400 uppercase">Cari</div>
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">{secili.unvan}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 uppercase">İşlem</div>
                <div className="text-sm font-bold tabular-nums">{fmtInt(filtreli.length)}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 uppercase">Toplam Litre</div>
                <div className="text-sm font-bold tabular-nums text-sky-600">{fmtInt(fLitre)} lt</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 uppercase">Toplam Tutar</div>
                <div className="text-sm font-bold tabular-nums text-emerald-600">{fmt(fTutar)} ₺</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => indir('excel')} disabled={!data?.islem_sayisi}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
                <FiDownload size={14} /> Excel
              </button>
              <button onClick={() => indir('pdf')} disabled={!data?.islem_sayisi}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40">
                <FiFileText size={14} /> PDF
              </button>
            </div>
          </div>

          {/* Ürün + Şube filtreleri */}
          {(urunler.length > 1 || subeler.length > 1) && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {urunler.length > 1 && (
                <div className="flex items-center gap-1">
                  <span className="text-[11px] font-semibold text-gray-500">Ürün:</span>
                  <button onClick={() => setUrunF('')}
                    className={`px-2.5 py-1 text-xs rounded-full border ${urunF === '' ? 'bg-sky-600 text-white border-sky-600' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Tümü</button>
                  {urunler.map(u => (
                    <button key={u} onClick={() => setUrunF(u)}
                      className={`px-2.5 py-1 text-xs rounded-full border ${urunF === u ? 'bg-sky-600 text-white border-sky-600' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{u}</button>
                  ))}
                </div>
              )}
              {subeler.length > 1 && (
                <div className="flex items-center gap-1">
                  <span className="text-[11px] font-semibold text-gray-500 ml-2">Şube:</span>
                  <button onClick={() => setSubeF('')}
                    className={`px-2.5 py-1 text-xs rounded-full border ${subeF === '' ? 'bg-slate-600 text-white border-slate-600' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Tümü</button>
                  {subeler.map(s => (
                    <button key={s} onClick={() => setSubeF(s)}
                      className={`px-2.5 py-1 text-xs rounded-full border ${subeF === s ? 'bg-slate-600 text-white border-slate-600' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{s}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tablo */}
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                <tr className="text-left text-gray-500 dark:text-gray-300">
                  <th onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                    className="px-3 py-2 font-semibold whitespace-nowrap cursor-pointer select-none hover:text-sky-600">
                    <span className="inline-flex items-center gap-1">İşlem Tarihi
                      {sortDir === 'desc' ? <FiChevronDown size={13} className="text-sky-500" /> : <FiChevronUp size={13} className="text-sky-500" />}</span>
                  </th>
                  <th className="px-3 py-2 font-semibold">Ürün</th>
                  <th className="px-3 py-2 font-semibold">Plaka</th>
                  <th className="px-3 py-2 font-semibold text-right">Litre</th>
                  <th className="px-3 py-2 font-semibold text-right">Tutar</th>
                  <th className="px-3 py-2 font-semibold text-right">Litre Fiyatı</th>
                  <th className="px-3 py-2 font-semibold">Şube</th>
                </tr>
              </thead>
              <tbody>
                {yukleniyor && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Yükleniyor…</td></tr>}
                {!yukleniyor && filtreli.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Bu dönemde işlem yok.</td></tr>}
                {!yukleniyor && [...filtreli].sort((a, b) => sortDir === 'desc' ? String(b.tarih).localeCompare(String(a.tarih)) : String(a.tarih).localeCompare(String(b.tarih))).map((r, i) => (
                  <tr key={i} className="border-t border-gray-50 dark:border-gray-700 hover:bg-sky-50/40 dark:hover:bg-sky-900/10">
                    <td className="px-3 py-1.5 whitespace-nowrap tabular-nums text-gray-600 dark:text-gray-300">{r.tarih}</td>
                    <td className="px-3 py-1.5">{r.urun}</td>
                    <td className="px-3 py-1.5 font-medium">{r.plaka}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.litre)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt(r.tutar)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{fmt(r.fiyat)}</td>
                    <td className="px-3 py-1.5 text-gray-500">{r.sube}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
